import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ProjectDetails.css';

interface DailyStatus {
  id: number;
  date: string;
  description: string;
  images: string[];
}

interface FurnitureItem {
  id: number;
  itemNo: number;
  name: string;
  quantity: number;
  estimatedCost: string;
  status: 'Pending' | 'Ordered' | 'Delivered';
  image?: string;
}

interface QuotationItem {
  category: string;
  amount: number;
  files?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
}

interface ProjectData {
  id: number;
  name: string;
  location: string;
  status: string;
  startDate: string;
  budget: string;
  quotationItems?: string;
  paidAmount?: number;
  dailyStatuses?: string;
  furnitureItems?: string;
  quotationSummaryFile?: string;
  furnitureSummaryFile?: string;
}

const ProjectDetails: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clientId, projectId } = useParams<{ clientId: string; projectId: string }>();
  const [activeTab, setActiveTab] = useState<'quotation' | 'daily-status' | 'furniture'>('quotation');

  // Project data
  const [project, setProject] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(true);

  // Currency state
  const [currency, setCurrency] = useState<'INR' | 'USD' | 'EUR'>('INR');
  const [isEditingQuotation, setIsEditingQuotation] = useState(false);

  // Exchange rates (you can fetch these from an API in production)
  const exchangeRates = {
    INR: 1,
    USD: 0.012,  // 1 INR = 0.012 USD
    EUR: 0.011   // 1 INR = 0.011 EUR
  };

  const currencySymbols = {
    INR: '₹',
    USD: '$',
    EUR: '€'
  };

  // Quotation data with numeric amounts
  const [paidAmount, setPaidAmount] = useState(0);
  const [quotationItems, setQuotationItems] = useState<QuotationItem[]>([
    { category: 'Construction Materials', amount: 0, files: [] },
    { category: 'Labor Charges', amount: 0, files: [] },
    { category: 'Interior Work', amount: 0, files: [] },
    { category: 'Electrical & Plumbing', amount: 0, files: [] },
    { category: 'Miscellaneous', amount: 0, files: [] },
  ]);

  // Summary files - changed to arrays to support multiple files
  const [quotationSummaryFiles, setQuotationSummaryFiles] = useState<Array<{ name: string; url: string; type: string }>>([]);
  const [furnitureSummaryFile, setFurnitureSummaryFile] = useState<{ name: string; url: string; type: string } | null>(null);

  const totalBudget = quotationItems.reduce((sum, item) => sum + item.amount, 0);
  const remainingAmount = totalBudget - paidAmount;

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/client/${clientId}`);
      const projectData = response.data.find((p: ProjectData) => p.id === parseInt(projectId!));
      if (projectData) {
        setProject(projectData);

        // Load quotation data if it exists
        if (projectData.quotationItems) {
          try {
            const parsedItems = JSON.parse(projectData.quotationItems);
            setQuotationItems(parsedItems);
          } catch (e) {
            console.error('Error parsing quotation items:', e);
          }
        }
        if (projectData.paidAmount !== undefined && projectData.paidAmount !== null) {
          setPaidAmount(projectData.paidAmount);
        }

        // Load daily statuses if they exist
        if (projectData.dailyStatuses) {
          try {
            const parsedStatuses = JSON.parse(projectData.dailyStatuses);
            setDailyStatuses(parsedStatuses);
          } catch (e) {
            console.error('Error parsing daily statuses:', e);
          }
        }

        // Load furniture items if they exist
        if (projectData.furnitureItems) {
          try {
            const parsedFurniture = JSON.parse(projectData.furnitureItems);

            // Assign itemNo to items that don't have it and sort by itemNo
            const itemsWithNumbers = parsedFurniture.map((item: FurnitureItem, index: number) => ({
              ...item,
              itemNo: item.itemNo || index + 1
            }));

            // Sort by itemNo in ascending order
            itemsWithNumbers.sort((a: FurnitureItem, b: FurnitureItem) => a.itemNo - b.itemNo);

            setFurnitureItems(itemsWithNumbers);
          } catch (e) {
            console.error('Error parsing furniture items:', e);
          }
        }

        // Load summary files if they exist
        if (projectData.quotationSummaryFile) {
          try {
            const parsedFiles = JSON.parse(projectData.quotationSummaryFile);
            // Handle both old single file format and new array format
            if (Array.isArray(parsedFiles)) {
              setQuotationSummaryFiles(parsedFiles);
            } else if (parsedFiles) {
              // Convert old single file to array
              setQuotationSummaryFiles([parsedFiles]);
            }
          } catch (e) {
            console.error('Error parsing quotation summary file:', e);
          }
        }

        if (projectData.furnitureSummaryFile) {
          try {
            const parsedFile = JSON.parse(projectData.furnitureSummaryFile);
            setFurnitureSummaryFile(parsedFile);
          } catch (e) {
            console.error('Error parsing furniture summary file:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const [dailyStatuses, setDailyStatuses] = useState<DailyStatus[]>([]);

  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editingStatusId, setEditingStatusId] = useState<number | null>(null);
  const [newStatus, setNewStatus] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    imageUrls: ''
  });
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  const [furnitureItems, setFurnitureItems] = useState<FurnitureItem[]>([]);

  const [showFurnitureModal, setShowFurnitureModal] = useState(false);
  const [isEditingFurniture, setIsEditingFurniture] = useState(false);
  const [editingFurnitureId, setEditingFurnitureId] = useState<number | null>(null);
  const [newFurniture, setNewFurniture] = useState({
    itemNo: 1,
    name: '',
    quantity: 1,
    estimatedCost: '',
    status: 'Pending' as 'Pending' | 'Ordered' | 'Delivered',
    image: ''
  });

  const getFurnitureStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return '#FFA726';
      case 'Ordered': return '#42A5F5';
      case 'Delivered': return '#66BB6A';
      default: return '#999';
    }
  };

  const handleFurnitureImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const maxWidth = 600;
          const scale = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scale;

          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressedImage = canvas.toDataURL('image/jpeg', 0.3);

          setNewFurniture({ ...newFurniture, image: compressedImage });
        };
        img.src = event.target.result as string;
      }
    };
    reader.readAsDataURL(file);
  };

  // Format currency with conversion
  const formatCurrency = (amountInINR: number) => {
    const convertedAmount = amountInINR * exchangeRates[currency];
    const symbol = currencySymbols[currency];

    if (currency === 'INR') {
      // Indian numbering system
      return `${symbol}${convertedAmount.toLocaleString('en-IN')}`;
    } else {
      // International numbering system
      return `${symbol}${convertedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const handleUpdateQuotationItem = (index: number, newAmount: number) => {
    const updatedItems = [...quotationItems];
    updatedItems[index].amount = newAmount;
    setQuotationItems(updatedItems);
  };

  const handleQuotationFileUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const newFiles: Array<{ name: string; url: string; type: string }> = [];

    for (const file of fileArray) {
      if (file.type === 'application/pdf' ||
          file.type === 'application/vnd.ms-excel' ||
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {

        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = (event) => {
            if (event.target?.result) {
              newFiles.push({
                name: file.name,
                url: event.target.result as string,
                type: file.type
              });
            }
            resolve(null);
          };
          reader.readAsDataURL(file);
        });
      }
    }

    if (newFiles.length > 0) {
      const updatedItems = [...quotationItems];
      updatedItems[index].files = [...(updatedItems[index].files || []), ...newFiles];
      setQuotationItems(updatedItems);

      const currentItem = updatedItems[index];
      console.log(`Category: ${currentItem.category}`);
      console.log(`Total files now: ${currentItem.files?.length || 0}`);
      console.log('File names:', currentItem.files?.map(f => f.name) || []);

      // Show success message
      alert(`${newFiles.length} file(s) uploaded successfully! Total files for ${currentItem.category}: ${currentItem.files?.length || 0}`);
    }

    // Reset the input so the same files can be selected again if needed
    e.target.value = '';
  };

  const handleRemoveQuotationFile = (itemIndex: number, fileIndex: number) => {
    const updatedItems = [...quotationItems];
    updatedItems[itemIndex].files?.splice(fileIndex, 1);
    setQuotationItems(updatedItems);
  };

  // Handle quotation summary file upload - supports multiple files
  const handleQuotationSummaryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const newFiles: Array<{ name: string; url: string; type: string }> = [];

    for (const file of fileArray) {
      if (file.type === 'application/pdf' ||
          file.type === 'application/vnd.ms-excel' ||
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {

        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = (event) => {
            if (event.target?.result) {
              newFiles.push({
                name: file.name,
                url: event.target.result as string,
                type: file.type
              });
            }
            resolve(null);
          };
          reader.readAsDataURL(file);
        });
      }
    }

    if (newFiles.length > 0) {
      const updatedFiles = [...quotationSummaryFiles, ...newFiles];
      setQuotationSummaryFiles(updatedFiles);

      // Save to database immediately
      try {
        await api.put(`/projects/${projectId}/quotation`, {
          quotationSummaryFile: JSON.stringify(updatedFiles)
        });
        alert(`${newFiles.length} file(s) uploaded successfully! Total files: ${updatedFiles.length}`);
      } catch (error) {
        console.error('Error saving quotation summary file:', error);
        alert('Failed to save files');
      }
    }

    // Reset input
    e.target.value = '';
  };

  // Handle furniture summary file upload
  const handleFurnitureSummaryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type === 'application/pdf' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {

      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target?.result) {
          const newFile = {
            name: file.name,
            url: event.target.result as string,
            type: file.type
          };
          setFurnitureSummaryFile(newFile);

          // Save to database immediately
          try {
            await api.put(`/projects/${projectId}/furniture-items`, {
              furnitureSummaryFile: JSON.stringify(newFile)
            });
          } catch (error) {
            console.error('Error saving furniture summary file:', error);
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveQuotation = async () => {
    try {
      // Save quotation data
      await api.put(`/projects/${projectId}/quotation`, {
        quotationItems: JSON.stringify(quotationItems),
        paidAmount: paidAmount
      });

      // Update project budget to match the total budget from quotation
      const formattedBudget = `₹${totalBudget.toLocaleString('en-IN')}`;
      await api.put(`/projects/${projectId}`, {
        budget: formattedBudget
      });

      // Refresh project data to show updated budget
      await fetchProjectData();

      setIsEditingQuotation(false);
      alert('Quotation saved successfully!');
    } catch (error: any) {
      console.error('Error saving quotation:', error);
      alert(error.response?.data?.message || 'Failed to save quotation');
    }
  };

  const handleAddDailyStatus = async () => {
    if (!newStatus.description) {
      alert('Please enter a description');
      return;
    }

    // Combine URL-based images and uploaded/pasted images
    const urlImages = newStatus.imageUrls
      ? newStatus.imageUrls.split(',').map(url => url.trim()).filter(url => url)
      : [];
    const allImages = [...selectedImages, ...urlImages];

    let updatedStatuses;
    if (isEditingStatus && editingStatusId !== null) {
      // Edit existing status
      updatedStatuses = dailyStatuses.map(status =>
        status.id === editingStatusId
          ? { ...status, date: newStatus.date, description: newStatus.description, images: allImages }
          : status
      );
    } else {
      // Add new status
      const newDailyStatus: DailyStatus = {
        id: dailyStatuses.length > 0 ? Math.max(...dailyStatuses.map(s => s.id)) + 1 : 1,
        date: newStatus.date,
        description: newStatus.description,
        images: allImages
      };
      updatedStatuses = [newDailyStatus, ...dailyStatuses];
    }

    try {
      // Save to backend
      await api.put(`/projects/${projectId}/daily-statuses`, {
        dailyStatuses: JSON.stringify(updatedStatuses)
      });

      setDailyStatuses(updatedStatuses);
      setShowAddStatusModal(false);
      setIsEditingStatus(false);
      setEditingStatusId(null);
      setSelectedImages([]);
      setNewStatus({
        date: new Date().toISOString().split('T')[0],
        description: '',
        imageUrls: ''
      });
      alert(isEditingStatus ? 'Daily status updated successfully!' : 'Daily status added successfully!');
    } catch (error: any) {
      console.error('Error saving daily status:', error);
      alert(error.response?.data?.message || 'Failed to save daily status');
    }
  };

  const handleEditStatus = (status: DailyStatus) => {
    setIsEditingStatus(true);
    setEditingStatusId(status.id);
    setNewStatus({
      date: status.date,
      description: status.description,
      imageUrls: ''
    });
    // Load existing images into selectedImages for preview
    setSelectedImages(status.images || []);
    setShowAddStatusModal(true);
  };

  const handleDeleteStatus = async (statusId: number) => {
    if (window.confirm('Are you sure you want to delete this daily status?')) {
      const updatedStatuses = dailyStatuses.filter(status => status.id !== statusId);

      try {
        // Save to backend
        await api.put(`/projects/${projectId}/daily-statuses`, {
          dailyStatuses: JSON.stringify(updatedStatuses)
        });

        setDailyStatuses(updatedStatuses);
        alert('Daily status deleted successfully!');
      } catch (error: any) {
        console.error('Error deleting daily status:', error);
        alert(error.response?.data?.message || 'Failed to delete daily status');
      }
    }
  };

  // Compress image to reduce payload size
  const compressImage = (file: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set max dimensions (reduced for smaller payload)
          const MAX_WIDTH = 600;
          const MAX_HEIGHT = 600;

          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress to JPEG with 0.3 quality (maximum compression)
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.3);
          resolve(compressedDataUrl);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImagePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          try {
            const compressedDataUrl = await compressImage(blob);
            setSelectedImages(prev => [...prev, compressedDataUrl]);
          } catch (error) {
            console.error('Error compressing pasted image:', error);
            alert('Failed to process pasted image');
          }
        }
      }
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        try {
          const compressedDataUrl = await compressImage(file);
          setSelectedImages(prev => [...prev, compressedDataUrl]);
        } catch (error) {
          console.error('Error compressing selected image:', error);
          alert('Failed to process selected image');
        }
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Furniture handlers
  const handleAddFurniture = async () => {
    if (!newFurniture.name || !newFurniture.estimatedCost) {
      alert('Please fill in all required fields');
      return;
    }

    let updatedItems;
    if (isEditingFurniture && editingFurnitureId !== null) {
      // Edit existing item
      const editingItem = furnitureItems.find(item => item.id === editingFurnitureId);
      const oldItemNo = editingItem?.itemNo || 0;
      const newItemNo = newFurniture.itemNo;

      // Update the item with new values
      updatedItems = furnitureItems.map(item =>
        item.id === editingFurnitureId
          ? { ...item, ...newFurniture }
          : item
      );

      // If itemNo changed, reorder other items
      if (oldItemNo !== newItemNo) {
        updatedItems = updatedItems.map(item => {
          if (item.id === editingFurnitureId) {
            return item; // Keep the edited item's new itemNo
          }

          if (oldItemNo < newItemNo) {
            // Item moved down: shift items up between old and new positions
            if (item.itemNo > oldItemNo && item.itemNo <= newItemNo) {
              return { ...item, itemNo: item.itemNo - 1 };
            }
          } else {
            // Item moved up: shift items down between new and old positions
            if (item.itemNo >= newItemNo && item.itemNo < oldItemNo) {
              return { ...item, itemNo: item.itemNo + 1 };
            }
          }
          return item;
        });
      }
    } else {
      // Add new item
      const newId = furnitureItems.length > 0 ? Math.max(...furnitureItems.map(i => i.id)) + 1 : 1;
      const targetItemNo = newFurniture.itemNo;

      // Shift existing items at or after the target position
      updatedItems = furnitureItems.map(item => {
        if (item.itemNo >= targetItemNo) {
          return { ...item, itemNo: item.itemNo + 1 };
        }
        return item;
      });

      // Add the new item
      const newItem: FurnitureItem = {
        id: newId,
        ...newFurniture
      };
      updatedItems = [...updatedItems, newItem];
    }

    // Sort by itemNo
    updatedItems.sort((a, b) => a.itemNo - b.itemNo);

    try {
      // Save to backend
      await api.put(`/projects/${projectId}/furniture-items`, {
        furnitureItems: JSON.stringify(updatedItems)
      });

      setFurnitureItems(updatedItems);
      setShowFurnitureModal(false);
      setIsEditingFurniture(false);
      setEditingFurnitureId(null);

      // Reset form with next available itemNo
      const maxItemNo = updatedItems.length > 0 ? Math.max(...updatedItems.map(i => i.itemNo)) : 0;
      setNewFurniture({ itemNo: maxItemNo + 1, name: '', quantity: 1, estimatedCost: '', status: 'Pending', image: '' });

      alert(isEditingFurniture ? 'Furniture item updated successfully!' : 'Furniture item added successfully!');
    } catch (error: any) {
      console.error('Error saving furniture item:', error);
      alert(error.response?.data?.message || 'Failed to save furniture item');
    }
  };

  const handleEditFurniture = (item: FurnitureItem) => {
    setIsEditingFurniture(true);
    setEditingFurnitureId(item.id);
    setNewFurniture({
      itemNo: item.itemNo,
      name: item.name,
      quantity: item.quantity,
      estimatedCost: item.estimatedCost,
      status: item.status,
      image: item.image || ''
    });
    setShowFurnitureModal(true);
  };

  const handleDeleteFurniture = async (itemId: number) => {
    if (window.confirm('Are you sure you want to delete this furniture item?')) {
      const deletedItem = furnitureItems.find(item => item.id === itemId);
      let updatedItems = furnitureItems.filter(item => item.id !== itemId);

      // Shift items down to fill the gap
      if (deletedItem) {
        updatedItems = updatedItems.map(item => {
          if (item.itemNo > deletedItem.itemNo) {
            return { ...item, itemNo: item.itemNo - 1 };
          }
          return item;
        });
      }

      try {
        // Save to backend
        await api.put(`/projects/${projectId}/furniture-items`, {
          furnitureItems: JSON.stringify(updatedItems)
        });

        setFurnitureItems(updatedItems);
        alert('Furniture item deleted successfully!');
      } catch (error: any) {
        console.error('Error deleting furniture item:', error);
        alert(error.response?.data?.message || 'Failed to delete furniture item');
      }
    }
  };

  if (loading) {
    return (
      <div className="project-details-container">
        <div style={{ textAlign: 'center', padding: '100px 40px', color: '#666' }}>
          <p style={{ fontSize: '18px' }}>Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-details-container">
        <div style={{ textAlign: 'center', padding: '100px 40px', color: '#666' }}>
          <p style={{ fontSize: '18px' }}>Project not found</p>
          <button onClick={() => navigate('/dashboard')} style={{ marginTop: '20px', padding: '10px 20px', borderRadius: '6px', border: 'none', background: '#667eea', color: 'white', cursor: 'pointer' }}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details-container">
      <header className="project-details-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => {
            // If user is a client, navigate to dashboard; if architect, navigate to client projects list
            if (user?.role === 'client') {
              navigate('/dashboard');
            } else {
              navigate(`/client/${clientId}/projects`);
            }
          }}>
            ← Back to Projects
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          <h1>{project.name}</h1>
          <p className="project-location">📍 {project.location}</p>
        </div>
      </header>

      <div className="project-tabs">
        <button
          className={`tab-btn ${activeTab === 'quotation' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotation')}
        >
          💰 Quotation
        </button>
        <button
          className={`tab-btn ${activeTab === 'daily-status' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily-status')}
        >
          📸 Daily Status
        </button>
        <button
          className={`tab-btn ${activeTab === 'furniture' ? 'active' : ''}`}
          onClick={() => setActiveTab('furniture')}
        >
          🪑 Interior Furniture
        </button>
      </div>

      <div className="project-content">
        {activeTab === 'quotation' && (
          <div className="quotation-section">
            <div className="quotation-controls">
              <div className="currency-selector">
                <label>Currency: </label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value as 'INR' | 'USD' | 'EUR')}>
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              {!isEditingQuotation ? (
                <button className="btn-edit-quotation" onClick={() => setIsEditingQuotation(true)}>
                  ✏️ Edit Quotation
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="btn-save-quotation" onClick={handleSaveQuotation}>
                    ✓ Save
                  </button>
                  <button className="btn-cancel-edit" onClick={() => setIsEditingQuotation(false)}>
                    ✕ Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="quotation-summary">
              <div className="summary-card total">
                <h3>TOTAL BUDGET</h3>
                <p className="amount">{formatCurrency(totalBudget)}</p>
              </div>
              <div className="summary-card paid">
                <h3>PAID AMOUNT</h3>
                {isEditingQuotation ? (
                  <input
                    type="number"
                    className="amount-input"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
                  />
                ) : (
                  <p className="amount">{formatCurrency(paidAmount)}</p>
                )}
              </div>
              <div className="summary-card remaining">
                <h3>REMAINING</h3>
                <p className="amount">{formatCurrency(remainingAmount)}</p>
              </div>
            </div>

            <div className="quotation-breakdown">
              <h2>Cost Breakdown</h2>
              <div className="breakdown-list">
                {quotationItems.map((item, index) => (
                  <div key={index} className="breakdown-item-container">
                    <div className="breakdown-item">
                      <span className="category">{item.category}</span>
                      {isEditingQuotation ? (
                        <input
                          type="number"
                          className="amount-edit-input"
                          value={item.amount}
                          onChange={(e) => handleUpdateQuotationItem(index, parseFloat(e.target.value) || 0)}
                        />
                      ) : (
                        <span className="amount">{formatCurrency(item.amount)}</span>
                      )}
                    </div>

                    {isEditingQuotation && (
                      <div style={{ marginTop: '10px', marginLeft: '20px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          color: '#666',
                          marginBottom: '5px',
                          fontWeight: '500'
                        }}>
                          Upload Documents (PDF/Excel):
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.xls,.xlsx"
                          multiple
                          onChange={(e) => handleQuotationFileUpload(index, e)}
                          style={{
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            background: '#f5f7fa'
                          }}
                        />
                        <div style={{
                          fontSize: '12px',
                          color: '#888',
                          marginTop: '5px',
                          fontStyle: 'italic'
                        }}>
                          💡 Tip: Hold Ctrl (Windows) or Cmd (Mac) and click to select multiple files, or click this button multiple times to add files one by one
                        </div>
                      </div>
                    )}

                    {item.files && item.files.length > 0 && (
                      <div style={{
                        marginTop: '10px',
                        marginLeft: '20px',
                        padding: '10px',
                        background: '#f9fafb',
                        borderRadius: '6px'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#555',
                          marginBottom: '8px'
                        }}>
                          Uploaded Files:
                        </div>
                        {item.files.map((file, fileIndex) => (
                          <div key={fileIndex} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 10px',
                            background: 'white',
                            borderRadius: '4px',
                            marginBottom: '5px',
                            border: '1px solid #e5e7eb'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '18px' }}>
                                {file.type.includes('pdf') ? '📄' : '📊'}
                              </span>
                              <span style={{ fontSize: '13px', color: '#333' }}>
                                {file.name}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <a
                                href={file.url}
                                download={file.name}
                                style={{
                                  padding: '4px 10px',
                                  background: '#667eea',
                                  color: 'white',
                                  borderRadius: '4px',
                                  textDecoration: 'none',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                Download
                              </a>
                              {isEditingQuotation && (
                                <button
                                  onClick={() => handleRemoveQuotationFile(index, fileIndex)}
                                  style={{
                                    padding: '4px 10px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500'
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Quotation Summary File Upload */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                📄 Final Quotation Summary
              </h3>
              <div>
                <input
                  type="file"
                  accept=".pdf,.xls,.xlsx"
                  multiple
                  onChange={handleQuotationSummaryUpload}
                  style={{ display: 'none' }}
                  id="quotation-summary-upload"
                />
                <label
                  htmlFor="quotation-summary-upload"
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: 'none',
                    display: 'inline-block',
                    marginBottom: '10px'
                  }}
                >
                  📎 Upload Summary (PDF/Excel)
                </label>
                <div style={{
                  fontSize: '12px',
                  color: '#888',
                  marginBottom: '15px',
                  fontStyle: 'italic'
                }}>
                  💡 Tip: Hold Ctrl (Windows) or Cmd (Mac) and click to select multiple files, or click this button multiple times to add files one by one
                </div>
                {quotationSummaryFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {quotationSummaryFiles.map((file, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 15px',
                        background: 'white',
                        borderRadius: '6px',
                        border: '1px solid #ddd'
                      }}>
                        <span style={{ fontSize: '20px' }}>
                          {file.type === 'application/pdf' ? '📕' : '📗'}
                        </span>
                        <span style={{ fontSize: '14px', color: '#333', flex: 1 }}>{file.name}</span>
                        <a
                          href={file.url}
                          download={file.name}
                          style={{
                            padding: '6px 12px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Download
                        </a>
                        <button
                          onClick={async () => {
                            const updatedFiles = quotationSummaryFiles.filter((_, i) => i !== index);
                            setQuotationSummaryFiles(updatedFiles);
                            await api.put(`/projects/${projectId}/quotation`, {
                              quotationSummaryFile: updatedFiles.length > 0 ? JSON.stringify(updatedFiles) : null
                            });
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                Upload the complete quotation summary document (PDF or Excel format)
              </p>
            </div>
          </div>
        )}

        {activeTab === 'daily-status' && (
          <div className="daily-status-section">
            <button className="btn-add-status" onClick={() => setShowAddStatusModal(true)}>+ Add Daily Status</button>
            <div className="status-timeline">
              {dailyStatuses.map((status) => (
                <div key={status.id} className="status-card">
                  <div className="status-header">
                    <h3>{new Date(status.date).toLocaleDateString('en-IN', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</h3>
                    <div className="status-actions">
                      <button className="btn-edit-status" onClick={() => handleEditStatus(status)} title="Edit status">
                        ✏️
                      </button>
                      <button className="btn-delete-status" onClick={() => handleDeleteStatus(status.id)} title="Delete status">
                        🗑️
                      </button>
                    </div>
                  </div>
                  <p className="status-description">{status.description}</p>
                  <div className="status-images">
                    {status.images.map((image, idx) => (
                      <img
                        key={idx}
                        src={image}
                        alt={`Site work ${idx + 1}`}
                        className="status-image"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          console.error(`Failed to load image ${idx + 1}`);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'furniture' && (
          <div className="furniture-section">
            <button className="btn-add-furniture" onClick={() => {
              const maxItemNo = furnitureItems.length > 0 ? Math.max(...furnitureItems.map(i => i.itemNo)) : 0;
              setNewFurniture({ itemNo: maxItemNo + 1, name: '', quantity: 1, estimatedCost: '', status: 'Pending', image: '' });
              setShowFurnitureModal(true);
            }}>+ Add Furniture Item</button>
            <div className="furniture-table">
              <table>
                <thead>
                  <tr>
                    <th>Item No</th>
                    <th>Picture</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Estimated Cost</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {furnitureItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.itemNo}</td>
                      <td>
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              const newWindow = window.open();
                              if (newWindow) {
                                newWindow.document.write(`
                                  <html>
                                    <head>
                                      <title>${item.name}</title>
                                      <style>
                                        body { margin: 0; padding: 20px; background: #000; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                                        img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                      </style>
                                    </head>
                                    <body>
                                      <img src="${item.image}" alt="${item.name}" />
                                    </body>
                                  </html>
                                `);
                                newWindow.document.close();
                              }
                            }}
                            title="Click to view full size"
                          />
                        ) : (
                          <div style={{
                            width: '60px',
                            height: '60px',
                            background: '#f0f0f0',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px'
                          }}>
                            📷
                          </div>
                        )}
                      </td>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.estimatedCost}</td>
                      <td>
                        <span
                          className="furniture-status-badge"
                          style={{ backgroundColor: getFurnitureStatusColor(item.status) }}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            className="btn-edit-status"
                            onClick={() => handleEditFurniture(item)}
                            title="Edit furniture item"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete-status"
                            onClick={() => handleDeleteFurniture(item.id)}
                            title="Delete furniture item"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Furniture Summary File Upload */}
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h3 style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                📋 Furniture Items Summary
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <input
                  type="file"
                  accept=".pdf,.xls,.xlsx"
                  onChange={handleFurnitureSummaryUpload}
                  style={{ display: 'none' }}
                  id="furniture-summary-upload"
                />
                <label
                  htmlFor="furniture-summary-upload"
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: 'none'
                  }}
                >
                  📎 Upload Summary (PDF/Excel)
                </label>
                {furnitureSummaryFile && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 15px',
                    background: 'white',
                    borderRadius: '6px',
                    border: '1px solid #ddd'
                  }}>
                    <span style={{ fontSize: '20px' }}>
                      {furnitureSummaryFile.type === 'application/pdf' ? '📕' : '📗'}
                    </span>
                    <span style={{ fontSize: '14px', color: '#333' }}>{furnitureSummaryFile.name}</span>
                    <a
                      href={furnitureSummaryFile.url}
                      download={furnitureSummaryFile.name}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Download
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        setFurnitureSummaryFile(null);
                        api.put(`/projects/${projectId}/furniture-items`, { furnitureSummaryFile: null });
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <p style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                Upload the complete furniture items summary document (PDF or Excel format)
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Daily Status Modal */}
      {showAddStatusModal && (
        <div className="modal-overlay" onClick={() => setShowAddStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditingStatus ? 'Edit Daily Status' : 'Add Daily Status'}</h2>
              <button className="btn-close" onClick={() => {
                setShowAddStatusModal(false);
                setIsEditingStatus(false);
                setEditingStatusId(null);
                setSelectedImages([]);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={newStatus.date}
                  onChange={(e) => setNewStatus({ ...newStatus, date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  placeholder="Describe the work progress for this day"
                  value={newStatus.description}
                  onChange={(e) => setNewStatus({ ...newStatus, description: e.target.value })}
                  rows={4}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div className="form-group">
                <label>Images (optional)</label>
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    marginRight: '10px'
                  }}>
                    📁 Choose Files
                  </label>
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    or paste images (Ctrl+V)
                  </small>
                </div>

                {selectedImages.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
                      {selectedImages.map((img, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img src={img} alt={`Preview ${idx + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '6px' }} />
                          <button
                            onClick={() => handleRemoveImage(idx)}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  placeholder="Or enter image URLs separated by commas"
                  value={newStatus.imageUrls}
                  onChange={(e) => setNewStatus({ ...newStatus, imageUrls: e.target.value })}
                  onPaste={handleImagePaste}
                  style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px' }}
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  You can upload files, paste images, or enter image URLs
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setShowAddStatusModal(false);
                setIsEditingStatus(false);
                setEditingStatusId(null);
                setSelectedImages([]);
              }}>Cancel</button>
              <button className="btn-submit" onClick={handleAddDailyStatus}>
                {isEditingStatus ? 'Update Status' : 'Add Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Furniture Modal */}
      {showFurnitureModal && (
        <div className="modal-overlay" onClick={() => setShowFurnitureModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditingFurniture ? 'Edit Furniture Item' : 'Add Furniture Item'}</h2>
              <button className="btn-close" onClick={() => {
                setShowFurnitureModal(false);
                setIsEditingFurniture(false);
                setEditingFurnitureId(null);
                const maxItemNo = furnitureItems.length > 0 ? Math.max(...furnitureItems.map(i => i.itemNo)) : 0;
                setNewFurniture({ itemNo: maxItemNo + 1, name: '', quantity: 1, estimatedCost: '', status: 'Pending', image: '' });
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Item No *</label>
                <input
                  type="number"
                  min="1"
                  placeholder="Enter item number"
                  value={newFurniture.itemNo}
                  onChange={(e) => setNewFurniture({ ...newFurniture, itemNo: parseInt(e.target.value) || 1 })}
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Change this number to reorder items
                </small>
              </div>
              <div className="form-group">
                <label>Item Name *</label>
                <input
                  type="text"
                  placeholder="Enter furniture item name"
                  value={newFurniture.name}
                  onChange={(e) => setNewFurniture({ ...newFurniture, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  min="1"
                  placeholder="Enter quantity"
                  value={newFurniture.quantity}
                  onChange={(e) => setNewFurniture({ ...newFurniture, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="form-group">
                <label>Estimated Cost *</label>
                <input
                  type="text"
                  placeholder="Enter estimated cost (e.g., ₹85,000)"
                  value={newFurniture.estimatedCost}
                  onChange={(e) => setNewFurniture({ ...newFurniture, estimatedCost: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select
                  value={newFurniture.status}
                  onChange={(e) => setNewFurniture({ ...newFurniture, status: e.target.value as 'Pending' | 'Ordered' | 'Delivered' })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Ordered">Ordered</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>
              <div className="form-group">
                <label>Item Picture</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFurnitureImageUpload}
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                />
                {newFurniture.image && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={newFurniture.image}
                      alt="Preview"
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #ddd'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setNewFurniture({ ...newFurniture, image: '' })}
                      style={{
                        marginLeft: '10px',
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Remove Image
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setShowFurnitureModal(false);
                setIsEditingFurniture(false);
                setEditingFurnitureId(null);
                const maxItemNo = furnitureItems.length > 0 ? Math.max(...furnitureItems.map(i => i.itemNo)) : 0;
                setNewFurniture({ itemNo: maxItemNo + 1, name: '', quantity: 1, estimatedCost: '', status: 'Pending', image: '' });
              }}>Cancel</button>
              <button className="btn-submit" onClick={handleAddFurniture}>
                {isEditingFurniture ? 'Update Item' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
